import React from "react";
import SEO from "@/components/SEO";
import CTAButton from "@/components/ui/CTAButton";
import { useCookieConsent } from "@/context/CookieConsentContext";

export default function PoliticaCookies() {
  const { openPreferences } = useCookieConsent();

  return (
    // El navbar es fijo (h-16 móvil, h-20 escritorio) y ahora, encima,
    // también la cinta promocional (~37px). Esta página no reservaba ningún
    // espacio propio: el titular quedaba tapado debajo.
    <main className="min-h-screen bg-card text-foreground pt-28 md:pt-32">
      <SEO
        title="Política de Cookies"
        description="Información sobre el uso de cookies en la web de la Delegación de Evangelización Digital de la Diócesis de Huelva."
      />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-[#1a3a6b] mb-2">
          Política de Cookies
        </h1>

        <p className="text-sm text-gray-500 mb-8">
          Fecha de entrada en vigor: 21/04/2026
        </p>

        <section className="space-y-6 leading-8 text-lg">
          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              1. ¿Qué son las cookies?
            </h2>
            <p>
              En la Delegación de Evangelización Digital utilizamos cookies y
              tecnologías similares para garantizar el correcto funcionamiento
              de nuestro sitio web evangelizaciondigitalhuelva.org, mejorar la
              experiencia del usuario y analizar el uso que se hace de la web.
            </p>
            <p>
              Las cookies son pequeños archivos de texto que se almacenan en su
              dispositivo (ordenador, móvil o tablet) cuando visita una página
              web. Permiten recordar información sobre su visita, como su idioma
              preferido o las páginas que ha visitado.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              2. Tipos de cookies que utilizamos
            </h2>

            <h3 className="text-xl font-semibold mt-4 mb-2">
              a) Cookies técnicas o necesarias
            </h3>
            <p>
              Son imprescindibles para el funcionamiento del sitio web y no
              requieren consentimiento.
            </p>
            <p>Ejemplos:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Gestión de sesión del usuario</li>
              <li>Seguridad y autenticación</li>
              <li>Configuración de privacidad</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-2">
              b) Cookies de análisis o medición
            </h3>
            <p>
              Permiten analizar el comportamiento de los usuarios en la web y
              mejorar los servicios ofrecidos.
            </p>
            <p>Ejemplos:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Páginas más visitadas</li>
              <li>Tiempo de navegación</li>
              <li>Errores detectados</li>
            </ul>
            <p>
              Estas cookies pueden ser propias o de terceros (como Google
              Analytics).
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-2">
              c) Cookies de personalización
            </h3>
            <p>
              Permiten recordar sus preferencias para ofrecer una experiencia
              más personalizada.
            </p>
            <p>Ejemplos:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Idioma seleccionado</li>
              <li>Configuración de visualización</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-2">
              d) Cookies publicitarias y de terceros
            </h3>
            <p>
              Se utilizan para mostrar anuncios relevantes y medir la eficacia
              de campañas publicitarias.
            </p>
            <p>
              Pueden ser instaladas por servicios externos como redes sociales o
              plataformas de contenido.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              3. ¿Cómo se utilizan las cookies en esta web?
            </h2>
            <p>Las cookies se emplean para:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Garantizar el correcto funcionamiento técnico del sitio</li>
              <li>Mejorar la navegación y experiencia del usuario</li>
              <li>Analizar el uso de la web para optimizar contenidos</li>
              <li>Facilitar la interacción con redes sociales</li>
              <li>Ofrecer contenidos adaptados a los intereses del usuario</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              4. Consentimiento
            </h2>
            <p>
              Al acceder por primera vez a nuestra web, se le mostrará un banner
              de cookies donde podrá:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Aceptar todas las cookies</li>
              <li>Rechazar las cookies no esenciales</li>
              <li>Configurar sus preferencias</li>
            </ul>
            <p>
              El consentimiento se puede modificar en cualquier momento desde el
              panel de configuración de cookies disponible en la web.
            </p>
            <CTAButton
              variant="outline"
              size="md"
              onClick={openPreferences}
              data-testid="politica-cookies-configurar"
              className="mt-2"
            >
              Configurar mis preferencias de cookies
            </CTAButton>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              5. ¿Cómo desactivar o eliminar cookies?
            </h2>
            <p>
              Puede permitir, bloquear o eliminar las cookies desde la
              configuración de su navegador:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Google Chrome</li>
              <li>Mozilla Firefox</li>
              <li>Safari</li>
              <li>Microsoft Edge</li>
            </ul>
            <p>
              Tenga en cuenta que, si desactiva algunas cookies, es posible que
              ciertas funciones de la web no estén disponibles o no funcionen
              correctamente.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              6. Cookies de terceros
            </h2>
            <p>
              Este sitio web puede utilizar servicios de terceros que recopilan
              información con fines estadísticos, de uso del sitio y para la
              prestación de otros servicios relacionados con la actividad del
              mismo.
            </p>
            <p>En particular, este sitio puede utilizar:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Google Analytics (análisis de uso)</li>
              <li>Integraciones con redes sociales (para compartir contenido)</li>
            </ul>
            <p>
              Estos terceros pueden transferir datos fuera del Espacio Económico
              Europeo, garantizando siempre niveles adecuados de protección
              conforme al RGPD.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              7. Actualizaciones de la política de cookies
            </h2>
            <p>
              Esta política puede actualizarse en función de cambios normativos
              o técnicos. Se recomienda revisarla periódicamente.
            </p>
            <p>
              En caso de cambios significativos, se informará a los usuarios
              mediante la web.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1a3a6b] mb-4">
              8. Contacto
            </h2>
            <p>
              Si tiene cualquier duda sobre el uso de cookies, puede
              contactarnos en:
            </p>
            <p>
              <a
                href="mailto:evangelizaciondigital@diocesisdehuelva.es"
                className="text-blue-700 underline"
              >
                evangelizaciondigital@diocesisdehuelva.es
              </a>
            </p>
            <p>
              <a
                href="tel:+34959252100"
                className="text-blue-700 underline"
              >
                +34 959252100
              </a>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
